import React, { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Input, InputNumber, Modal, Pagination, Spin, message } from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../i18n';
import { useSelectedShop } from '../layout/selectedShopContext';
import { addCollaborationProducts, getCommerceProducts } from '../../../store/request';
import {
  SHOP_PRODUCT_PAGE_SIZE,
  commissionRateToPercent,
  percentToCommissionRate,
  type ShopProduct
} from '../../../types/commerce';

type AddInviteProductModalProps = {
  open: boolean;
  shopId?: string | null;
  collaborationIds: number[];
  onClose: () => void;
  onSuccess?: () => void;
};

function productKey(item: ShopProduct, index: number, page: number) {
  return item.productId?.trim() || `product-${page}-${index}`;
}

export function AddInviteProductModal({
  open,
  shopId,
  collaborationIds,
  onClose,
  onSuccess
}: AddInviteProductModalProps) {
  const { t } = useTranslation();
  const { shops } = useSelectedShop();
  const [products, setProducts] = useState<ShopProduct[]>([]);
  const [productPage, setProductPage] = useState(1);
  const [productTotal, setProductTotal] = useState(0);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productReloadKey, setProductReloadKey] = useState(0);
  const [productKeywordInput, setProductKeywordInput] = useState('');
  const [productKeyword, setProductKeyword] = useState('');
  const appliedProductKeywordRef = useRef('');
  const refreshRequested = useRef(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [rates, setRates] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const currentShopId = shopId?.trim() || '';
  const shopName = shops.find((item) => item.shopId === currentShopId)?.shopName?.trim() || '—';

  useEffect(() => {
    if (!open) return;
    setSelectedIds([]);
    setRates({});
    setSubmitting(false);
    setProductPage(1);
    setProductTotal(0);
    setProductReloadKey(0);
    setProductKeywordInput('');
    setProductKeyword('');
    appliedProductKeywordRef.current = '';
  }, [open]);

  useEffect(() => {
    setProductPage(1);
    setSelectedIds([]);
    setRates({});
    setProductKeywordInput('');
    setProductKeyword('');
    appliedProductKeywordRef.current = '';
  }, [currentShopId]);

  useEffect(() => {
    if (!open) return;
    if (!currentShopId) {
      setProducts([]);
      setProductTotal(0);
      setProductsLoading(false);
      return;
    }
    let cancelled = false;
    setProductsLoading(true);
    const notify = refreshRequested.current;
    refreshRequested.current = false;
    const keyword = productKeyword.trim();
    getCommerceProducts({
      shopId: currentShopId,
      pageNum: productPage,
      pageSize: SHOP_PRODUCT_PAGE_SIZE,
      ...(keyword ? { keyword } : {})
    })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200) {
          if (res.code !== -1) message.error(res.message || t('invitation.product_load_failed'));
          setProducts([]);
          setProductTotal(0);
          return;
        }
        const records = res.content?.records;
        const total = res.content?.total;
        const list = Array.isArray(records) ? records : [];
        setProducts(list);
        setProductTotal(typeof total === 'number' ? total : list.length);
        setRates((prev) => {
          const next = { ...prev };
          list.forEach((item, index) => {
            const key = productKey(item, index, productPage);
            if (next[key] != null) return;
            const percent = commissionRateToPercent(item.commissionRate);
            if (percent != null) next[key] = percent;
          });
          return next;
        });
        if (notify) message.success(res.message || t('invitation.product_refreshed'));
      })
      .catch(() => {
        if (!cancelled) {
          message.error(t('invitation.product_load_failed'));
          setProducts([]);
          setProductTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) setProductsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, currentShopId, productPage, productKeyword, productReloadKey, t]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = productKeywordInput.trim();
      if (appliedProductKeywordRef.current === next) return;
      appliedProductKeywordRef.current = next;
      setProductKeyword(next);
      setProductPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [productKeywordInput]);

  const toggleProduct = (id: string, checked: boolean) => {
    setSelectedIds((prev) => (checked ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter((item) => item !== id)));
  };

  const setRate = (id: string, value: number | null) => {
    if (value === null) return;
    setRates((prev) => ({ ...prev, [id]: value }));
  };

  const refreshProducts = () => {
    if (!currentShopId) {
      message.warning(t('invitation.invite_shop_required'));
      return;
    }
    refreshRequested.current = true;
    setProductReloadKey((key) => key + 1);
  };

  const submit = async () => {
    if (submitting) return;
    if (!currentShopId) {
      message.warning(t('invitation.invite_shop_required'));
      return;
    }
    const inviteIds = Array.from(new Set(collaborationIds.filter((id) => Number.isInteger(id))));
    if (inviteIds.length === 0) {
      message.warning(t('invitation.invite_pick_required'));
      return;
    }
    if (inviteIds.length > 10) {
      message.warning(t('invitation.add_products_limit'));
      return;
    }
    const selectedProducts = selectedIds.map((id) => ({
      productId: id.startsWith('product-') ? '' : id,
      ratePercent: rates[id]
    }));
    if (selectedProducts.length === 0) {
      message.warning(t('invitation.pick_product'));
      return;
    }
    if (selectedProducts.some((item) => !item.productId || item.productId.length > 64)) {
      message.warning(t('invitation.product_id_invalid'));
      return;
    }
    if (
      selectedProducts.some(
        (item) =>
          typeof item.ratePercent !== 'number' ||
          !Number.isFinite(item.ratePercent) ||
          item.ratePercent < 0
      )
    ) {
      message.warning(t('invitation.commission_required'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await addCollaborationProducts({
        shopId: currentShopId,
        collaborationIds: inviteIds,
        products: selectedProducts.map((item) => ({
          productId: item.productId,
          targetCommissionRate: percentToCommissionRate(item.ratePercent as number)
        }))
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || t('invitation.add_products_failed'));
        return;
      }
      const result = res.content;
      const noneAdded =
        result != null &&
        (result.successCount ?? 0) === 0 &&
        (result.partialSuccessCount ?? 0) === 0 &&
        (result.failureCount ?? 0) > 0;
      if (noneAdded) {
        message.warning(res.message || t('invitation.add_products_failed'));
        return;
      }
      message.success(res.message || t('invitation.add_products_success'));
      onSuccess?.();
      onClose();
    } catch {
      message.error(t('invitation.add_products_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={t('invitation.add_product_title')}
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={680}
      closeIcon={
        <span className="cursor-pointer text-sm font-normal text-slate-500 hover:text-slate-700">{t('common.close')}</span>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button className="rounded-lg" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="primary" className="wb-btn-primary rounded-lg" loading={submitting} onClick={submit}>
            {t('invitation.add_product')}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="mb-0 text-xs leading-relaxed text-slate-400">{t('invitation.product_hint')}</p>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-slate-500">
            {t('invitation.current_shop')} <span className="font-medium text-slate-700">{shopName}</span>
            {' · '}
            {t('invitation.rate_adjustable')}
          </span>
          <Button
            size="small"
            className="rounded-lg"
            icon={<ReloadOutlined />}
            loading={productsLoading}
            onClick={refreshProducts}
          >
            {t('invitation.refresh')}
          </Button>
        </div>
        <Input
          allowClear
          className="rounded-lg"
          placeholder={t('invitation.product_keyword')}
          prefix={<SearchOutlined className="text-slate-400" />}
          value={productKeywordInput}
          onChange={(event) => setProductKeywordInput(event.target.value)}
        />

        <div className="overflow-hidden rounded-lg border border-[#eef0f4] bg-white">
          {productsLoading ? (
            <div className="flex justify-center py-6">
              <Spin size="small" />
            </div>
          ) : null}
          {!productsLoading && products.length === 0 ? (
            <p className="mb-0 px-3 py-6 text-center text-sm text-slate-400">
              {!currentShopId ? t('invitation.invite_shop_required') : t('invitation.product_empty')}
            </p>
          ) : null}
          {!productsLoading
            ? products.map((item, index) => {
                const id = productKey(item, index, productPage);
                const title = item.productName?.trim() || item.productId?.trim() || '—';
                return (
                  <div
                    key={id}
                    className="flex flex-wrap items-center gap-3 border-b border-[#eef0f4] px-3 py-3 last:border-b-0 sm:flex-nowrap"
                  >
                    <Checkbox
                      checked={selectedIds.includes(id)}
                      onChange={(e) => toggleProduct(id, e.target.checked)}
                    />
                    {item.mainImageUrl?.trim() ? (
                      <img
                        src={item.mainImageUrl}
                        alt=""
                        className="size-[56px] shrink-0 rounded-lg object-cover"
                      />
                    ) : (
                      <span className="inline-flex size-[56px] shrink-0 items-center justify-center rounded-lg bg-slate-100 text-[11px] font-semibold tracking-wide text-slate-400">
                        SKU
                      </span>
                    )}
                    <div className="min-w-0 w-[340px] shrink break-words text-[13px] font-semibold leading-snug text-slate-800">
                      {title}
                    </div>
                    <div className="ml-auto flex shrink-0 items-center gap-2 pl-2 sm:pl-0">
                      <span className="text-xs text-slate-500">{t('invitation.target_commission')}</span>
                      <InputNumber
                        min={0}
                        precision={0}
                        size="small"
                        className="w-[72px]"
                        value={rates[id]}
                        onChange={(value) => setRate(id, value)}
                      />
                      <span className="text-xs text-slate-400">%</span>
                    </div>
                  </div>
                );
              })
            : null}
          {!productsLoading && productTotal > SHOP_PRODUCT_PAGE_SIZE ? (
            <div className="flex justify-end border-t border-[#eef0f4] px-3 py-2">
              <Pagination
                size="small"
                current={productPage}
                pageSize={SHOP_PRODUCT_PAGE_SIZE}
                total={productTotal}
                showSizeChanger={false}
                onChange={(page) => setProductPage(page)}
              />
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
